import { Component, Input, OnInit } from '@angular/core';
import { AbstractControl, FormControl } from '@angular/forms';
import { TableColumnConfig, TableConfig, TableRowValue, buildInitialRows, parseTableValue } from '../table-config';

@Component({
  standalone: false,
  selector: 'wr-table-field',
  templateUrl: './table-field.component.html',
  styleUrls: ['./table-field.component.scss'],
})
export class TableFieldComponent implements OnInit {

  @Input() control!: AbstractControl;
  @Input() config!: TableConfig;

  rows: TableRowValue[] = [];

  get formControl(): FormControl {
    return this.control as FormControl;
  }

  get isDisabled(): boolean {
    return this.control.disabled;
  }

  ngOnInit(): void {
    const existing = parseTableValue(this.control.value);
    if (existing.length > 0) {
      // Merge existing values with current column config (columns may have changed)
      this.rows = this.mergeWithConfig(existing);
    } else {
      this.rows = buildInitialRows(this.config);
    }
    this.sync();

    this.control.valueChanges.subscribe(val => {
      if (this.control.pristine) {
        const parsed = parseTableValue(val);
        if (parsed.length > 0) {
          this.rows = this.mergeWithConfig(parsed);
        }
      }
    });
  }

  private mergeWithConfig(existing: TableRowValue[]): TableRowValue[] {
    return this.config.rows.map((rowCfg, i) => {
      const existingRow = existing.find(r => r.rowName === rowCfg.name) ?? existing[i];
      const cells: Record<string, string> = {};
      for (const col of this.config.columns) {
        cells[col.name] = existingRow?.cells?.[col.name] ?? '';
      }
      return { rowName: rowCfg.name, cells };
    }).concat(
      this.config.allowAddRows
        ? existing.filter(r => !this.config.rows.some(cr => cr.name === r.rowName)).map(r => ({
          rowName: r.rowName,
          cells: Object.fromEntries(this.config.columns.map(col => [col.name, r.cells?.[col.name] ?? ''])),
        }))
        : [],
    );
  }

  getCellValue(row: TableRowValue, colName: string): string {
    return row.cells[colName] ?? '';
  }

  setCellValue(row: TableRowValue, colName: string, value: string): void {
    row.cells[colName] = value;
    this.sync();
  }

  addRow(): void {
    const newRow: TableRowValue = {
      rowName: '',
      cells: Object.fromEntries(this.config.columns.map(col => [col.name, ''])),
    };
    this.rows = [...this.rows, newRow];
    this.sync();
  }

  setRowName(index: number, name: string): void {
    this.rows[index].rowName = name;
    this.sync();
  }

  trackByIndex(index: number): number {
    return index;
  }

  removeRow(index: number): void {
    this.rows = this.rows.filter((_, i) => i !== index);
    this.sync();
  }

  isFixedRow(index: number): boolean {
    return index < this.config.rows.length;
  }

  columnOptions(col: TableColumnConfig): string[] {
    return col.options ?? [];
  }

  private sync(): void {
    this.formControl.setValue(JSON.stringify(this.rows), { emitEvent: true });
    this.formControl.markAsDirty();
  }
}
